/* eslint-disable no-console */

import React, { useContext } from 'react';
import PropTypes from 'prop-types';
import { format, isPast, setHours, isSameHour, addDays } from 'date-fns';
import { Button } from '@bufferapp/ui';
import { Droppable } from 'react-beautiful-dnd';
import AddButton from '../AddButton';
import { PostItem } from '../PostItem';
import Hour from './style';
import createCalendarPostContext from '../hooks/createCalendarPostContext';

const Hours = ({
  startDate,
  is24HourFormat,
  intervals,
  numberDaysToDisplay,
  hour,
  data,
  loading,
  postLimit,
  isViewOnly,
  setOpenModal,
}) => {
  const { hasPermissions } = useContext(createCalendarPostContext);
  // Generates 7 cells in each of the 24 hour rows, one per day of the week
  return [...Array(numberDaysToDisplay)].map((_, index) => {
    const cellDay = addDays(startDate, index);
    const cellDayWithHour = setHours(cellDay, hour);

    let startOfHourTimestamp = cellDayWithHour.getTime() / 1000;
    const currentTimestamp = new Date().getTime() / 1000;
    // Use start of hour timestamp for later dates or add 5 mins to current timestamp
    const fiveMinutesFromCurrentTimestamp = currentTimestamp + 300;
    startOfHourTimestamp =
      startOfHourTimestamp > currentTimestamp
        ? startOfHourTimestamp
        : fiveMinutesFromCurrentTimestamp;

    // 2 PM or 14:00 hour label for first column
    const hoursWithLabels = [2, 4, 6, 8, 10, 12, 14, 16, 18, 20, 22];
    const hourLabelFormat = is24HourFormat ? 'H:00' : 'h a';
    const hourLabel = format(cellDayWithHour, hourLabelFormat);
    const firstColumn = index === 0;

    // Disables add buttons and changes styles if hours in the past
    const isCurrentHour = isSameHour(new Date(), cellDayWithHour);
    const isPastHour = isPast(cellDayWithHour);

    const showAddButton = (!isPastHour || isCurrentHour) && hasPermissions;

    // Posts data
    const intervalsForDay = data?.calendar?.days[index]?.intervals;
    const interval = intervalsForDay && intervalsForDay[hour];
    const limit = postLimit?.postsLimit?.[hour];
    const inLastTwoRows = hour >= 22;

    return (
      <Droppable
        droppableId={cellDayWithHour.getTime().toString()}
        key={`slot-${cellDayWithHour}`}
        isDropDisabled={isPastHour && !isCurrentHour}
      >
        {(provided, snapshot) => (
          <Hour
            hourLabel={hourLabel}
            showTime={firstColumn && hoursWithLabels.includes(hour)}
            isPast={isPastHour}
            isActive={isCurrentHour}
            ref={provided.innerRef}
            // eslint-disable-next-line react/jsx-props-no-spreading
            {...provided.droppableProps}
            isDraggingOver={snapshot.isDraggingOver}
          >
            {interval?.posts.map((post, postIndex) => {
              if (postIndex < limit) {
                return (
                  <PostItem
                    key={post.id}
                    postId={post.id}
                    isViewOnly={isViewOnly}
                    scheduledDay={format(cellDay, 'd MMMM EEEE yyyy')}
                    inLastTwoRows={inLastTwoRows}
                    index={postIndex}
                    isDraggingOver={snapshot.isDraggingOver}
                    setOpenModal={setOpenModal}
                  />
                );
              }
            })}
            {interval?.posts?.length > limit && !snapshot.isDraggingOver && (
              <Button
                type="text"
                tabIndex="0"
                label="+ More"
                onClick={() => postLimit?.showMorePosts(hour)}
              />
            )}
            {showAddButton && !snapshot.isDraggingOver && (
              <AddButton
                fullWidth
                defaultTimeslot={startOfHourTimestamp}
                labelDate={`${format(
                  cellDay,
                  'eeee, d MMMM y'
                )} at ${hourLabel}`}
              />
            )}
            {provided.placeholder}
          </Hour>
        )}
      </Droppable>
    );
  });
};

Hours.propTypes = {
  startDate: PropTypes.instanceOf(Date).isRequired,
  is24HourFormat: PropTypes.bool,
  intervals: PropTypes.arrayOf(
    PropTypes.shape({
      label: PropTypes.string,
      startTimestamp: PropTypes.number,
      posts: PropTypes.array,
    })
  ),
  numberDaysToDisplay: PropTypes.number,
  hour: PropTypes.number.isRequired,
  loading: PropTypes.bool,
  setOpenModal: PropTypes.func,
};

Hours.defaultProps = {
  is24HourFormat: false,
  numberDaysToDisplay: 7,
  loading: true,
  setOpenModal: null,
};

export default Hours;
